import { withRetry } from '@core';
import { fetchSwingVideos } from '@data';
import { globalStore } from '@store';

import { instantReplayActiveSwingIdAtom, instantReplayActiveVideosAtom, isAnalyzingAtom } from '../state/atoms';
import { FullSwing, PartialSwingCache } from '../state/PartialSwingCache';
import { fetchAndShapeAnalysis, loadFullAnalysis, reflectBeastStatus } from '../utils';

import {
    AnalysisFailedMessage,
    AnalysisMessage,
    BOOTH_MESSAGE,
    BeastMessageContent,
    BeastStatusMessage,
    FullAnalysisAvailableMessage,
    ImagesMessage,
    VideosMessage,
} from './schema';

export type BeastEventHandlers = {
    [K in keyof BeastMessageContent]: (content: BeastMessageContent[K]) => void | Promise<void>;
};

export type BeastHandlerContext = {
    finalizeSwing: (swing: FullSwing) => void;
    onActivityIdChange: (activityId: number | null) => void;
    userId: string;
};

const cache = new PartialSwingCache();

export const getMessageHandlers = ({
    finalizeSwing,
    onActivityIdChange,
    userId,
}: BeastHandlerContext): BeastEventHandlers => ({
    [BOOTH_MESSAGE.BEAST_STATUS]: (content: BeastStatusMessage) => {
        globalStore.set(isAnalyzingAtom, content.status === 'ANALYZING_SWING');
        onActivityIdChange(content.activity_id);
        reflectBeastStatus(content.status);
    },

    [BOOTH_MESSAGE.FULL_ANALYSIS_AVAILABLE]: (content: FullAnalysisAvailableMessage) => {
        loadFullAnalysis(content, userId);
    },

    [BOOTH_MESSAGE.SWING_VIDEOS_AVAILABLE]: async (content: VideosMessage) => {
        const videos = await withRetry(() => fetchSwingVideos(content.swing_uuid), 5, 500);
        const swing = cache.mergeVideos(content.swing_uuid, videos);
        globalStore.set(instantReplayActiveVideosAtom, videos);
        globalStore.set(instantReplayActiveSwingIdAtom, content.swing_uuid);

        if (swing) finalizeSwing(swing);
    },

    [BOOTH_MESSAGE.ANALYSIS_AVAILABLE]: async (content: AnalysisMessage) => {
        const analysis = await fetchAndShapeAnalysis(content.swing_uuid, content);
        const swing = cache.mergeAnalysis(content.swing_uuid, analysis);
        if (swing) finalizeSwing(swing);
    },

    [BOOTH_MESSAGE.POSITION_IMAGES_AVAILABLE]: (content: ImagesMessage) => {
        const images = content.urls ? Object.entries(content.urls).map(([name, url]) => ({ name, url })) : [];
        const swing = cache.mergeImages(content.swing_uuid, images);
        if (swing) finalizeSwing(swing);
    },

    [BOOTH_MESSAGE.SWING_ANALYSIS_FAILED]: (content: AnalysisFailedMessage) => {
        if (content.swing_uuid === null) return;
        reflectBeastStatus('SWING_ANALYSIS_FAILED');
        globalStore.set(isAnalyzingAtom, false);
    },
});

export function dispatch<K extends keyof BeastMessageContent>(
    handlers: BeastEventHandlers,
    type: K,
    content: BeastMessageContent[K],
): void | Promise<void> {
    const handler = handlers[type];
    if (!handler) {
        throw new Error(`No handler registered for Beast event: ${type}`);
    }
    return handler(content);
}
